import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import { useLanguage } from '../context/LanguageContext';
import './ProductDetail.css';

const SLIDE_WIDTH_RATIO = 0.72; /* pagrindinė nuotrauka ~72% pločio, likusį ~14% kiekvienoje pusėje – gretimų „peek“ */
const GALLERY_GAP_PX = 40; /* tarpas tarp nuotraukų (px) */

/** Pašalina inline style iš HTML, kad būtų naudojamas tik parduotuvės šriftas ir stiliai */
function stripInlineStyles(html) {
  if (!html || typeof html !== 'string') return '';
  if (typeof document === 'undefined') return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll('*').forEach((el) => el.removeAttribute('style'));
  return div.innerHTML;
}

function ProductDetail() {
  const { slug } = useParams();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t, language } = useLanguage();
  const lang = language === 'de' ? 'de' : language === 'en' ? 'en' : 'lt';

  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const galleryRef = React.useRef(null);
  const [galleryWidth, setGalleryWidth] = useState(0);
  const touchStartRef = React.useRef({ x: 0, y: 0 });
  const [dragOffsetPx, setDragOffsetPx] = useState(0);
  const dragOffsetPxRef = React.useRef(0);
  const mouseListenersRef = React.useRef(null);

  const { data: product, isLoading } = useQuery({
    queryKey: ['product', slug],
    queryFn: async () => {
      const res = await apiClient.get(`/products/${slug}`);
      return res.data;
    },
  });

  const productName = React.useMemo(
    () => (product ? (product[`name_${lang}`] || product.name_lt || product.name_en || product.name_de || product.name || '') : ''),
    [product, lang]
  );
  const productDescription = React.useMemo(
    () => (product ? (product[`description_${lang}`] || product.description_lt || product.description_en || product.description_de || product.description || '') : ''),
    [product, lang]
  );
  const productMetaTitle = product ? (product[`meta_title_${lang}`] || product.meta_title_lt || product.meta_title_en || product.meta_title_de || product.meta_title || productName) : '';
  const productMetaDescription = product ? (product[`meta_description_${lang}`] || product.meta_description_lt || product.meta_description_en || product.meta_description_de || product.meta_description || productDescription || '') : '';

  // Apskaičiuoti rodomą kainą (prieš useEffect)
  const displayPrice = React.useMemo(() => {
    if (selectedVariant) {
      return selectedVariant.salePrice || selectedVariant.price || 0;
    }
    return product?.salePrice || product?.price || 0;
  }, [product, selectedVariant]);

  // Apskaičiuoti stock quantity (prieš useEffect)
  const displayStock = React.useMemo(() => {
    if (selectedVariant) {
      return selectedVariant.stock_quantity || 0;
    }
    return product?.stock_quantity || 0;
  }, [product, selectedVariant]);

  // Sale informacija (prieš useEffect)
  const saleInfo = selectedVariant?.saleInfo || product?.saleInfo;

  // SEO: Schema markup ir canonical URL
  useEffect(() => {
    if (!product) return;

    // Canonical URL
    const canonicalUrl = product.canonical_url || window.location.href;
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    const metaTitle = productMetaTitle || product.name;
    document.title = metaTitle;

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', productMetaDescription || '');

    let schemaScript = document.querySelector('script[type="application/ld+json"][data-product-schema]');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.setAttribute('data-product-schema', 'true');
      document.head.appendChild(schemaScript);
    }

    const schemaData = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: productName || product.name,
      description: productDescription || product.description || product.short_description,
      image: product.images?.map(img => img.image_url) || [],
      sku: product.sku,
      brand: {
        '@type': 'Brand',
        name: 'Magtic'
      },
      offers: {
        '@type': 'Offer',
        url: window.location.href,
        priceCurrency: 'EUR',
        price: displayPrice,
        availability: displayStock > 0 
          ? 'https://schema.org/InStock' 
          : 'https://schema.org/OutOfStock',
        priceValidUntil: saleInfo?.endDate 
          ? new Date(saleInfo.endDate).toISOString().split('T')[0]
          : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      }
    };

    // Jei yra custom schema markup, pridėti jį
    if (product.schema_markup) {
      try {
        const customSchema = JSON.parse(product.schema_markup);
        Object.assign(schemaData, customSchema);
      } catch (e) {
      }
    }

    schemaScript.textContent = JSON.stringify(schemaData);

    // Cleanup
    return () => {
      // Nereikia šalinti, nes tai globalūs meta tagai
    };
  }, [product, productName, productDescription, productMetaTitle, productMetaDescription, displayPrice, displayStock, saleInfo]);

  // Grupuoti variantus pagal atributus (Spalva, Dydis ir kt.)
  const groupedVariants = React.useMemo(() => {
    if (!product?.variants || product.variants.length === 0) return null;

    const groups = {};
    product.variants.forEach(variant => {
      variant.attributes?.forEach(attr => {
        const name = attr.attribute_name || attr.attributeName;
        const value = attr.attribute_value || attr.attributeValue;
        if (!name || !value) return;
        if (!groups[name]) groups[name] = [];
        if (!groups[name].includes(value)) groups[name].push(value);
      });
    });
    return Object.keys(groups).length > 0 ? groups : null;
  }, [product]);

  // Ar turime variantus (su atributais arba tik pagal pavadinimą)
  const hasVariants = product?.variants && product.variants.length > 0;
  // Rodyti paprastą variantų sąrašą (pagal pavadinimą), kai nėra atributų
  const showSimpleVariantList = hasVariants && !groupedVariants;

  // Rasti variantą pagal pasirinktus atributus
  const findVariantByAttributes = (selectedAttributes) => {
    if (!product?.variants) return null;
    return product.variants.find(variant => {
      const attrs = variant.attributes || [];
      if (attrs.length === 0) return false;
      return attrs.every(attr => {
        const name = attr.attribute_name || attr.attributeName;
        const value = attr.attribute_value || attr.attributeValue;
        return value != null && selectedAttributes[name] === value;
      });
    }) || null;
  };

  // Atnaujinti variantą kai pasirenkami atributai
  const handleAttributeChange = (attributeName, attributeValue) => {
    const currentSelection = selectedVariant 
      ? Object.fromEntries(
          (selectedVariant.attributes || []).map(a => [a.attribute_name || a.attributeName, a.attribute_value || a.attributeValue])
        )
      : {};
    currentSelection[attributeName] = attributeValue;
    const variant = findVariantByAttributes(currentSelection);
    setSelectedVariant(variant);
    if (variant?.image_url) {
      const imageIndex = product.images?.findIndex(img => (img.image_url || img) === variant.image_url);
      if (imageIndex >= 0) setSelectedImageIndex(imageIndex);
    }
  };

  // Pasirinkti variantą iš paprasto sąrašo (be atributų grupiavimo)
  const handleSimpleVariantSelect = (variant) => {
    setSelectedVariant(prev => (prev?.id === variant.id ? null : variant));
    if (variant?.image_url) {
      const imageIndex = product.images?.findIndex(img => (img.image_url || img) === variant.image_url);
      if (imageIndex >= 0) setSelectedImageIndex(imageIndex);
    }
  };

  // Patikrinti ar produktas yra mėgstamų sąraše – tik prisijungusiems (svečiai nekreipia, kad negautų 401 ir nukreipimo į login)
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('token');
  useQuery({
    queryKey: ['favorite-check', product?.id],
    queryFn: async () => {
      if (!product?.id) return false;
      try {
        const res = await apiClient.get(`/user/favorites/check/${product.id}`);
        return res.data.isFavorite || false;
      } catch (error) {
        if (error.response?.status === 401) {
          return false; // Neautorizuotas vartotojas
        }
        return false;
      }
    },
    enabled: !!product?.id && !!hasToken,
    onSuccess: (data) => {
      setIsFavorite(data);
    },
  });

  // Pridėti į krepšelį
  const addToCartMutation = useMutation({
    mutationFn: async () => {
      const sessionId = localStorage.getItem('sessionId') || 'sess_' + Date.now();
      localStorage.setItem('sessionId', sessionId);
      
      return apiClient.post('/cart/add', {
        product_id: product.id,
        variant_id: selectedVariant?.id || null,
        quantity: quantity,
      }, {
        headers: {
          'x-session-id': sessionId,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['cart']);
      notification.success(t('addedToCart'));
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('errorAddingToCart'));
    },
  });

  // Pridėti/pašalinti iš mėgstamų
  const toggleFavoriteMutation = useMutation({
    mutationFn: async () => {
      if (isFavorite) {
        return apiClient.delete(`/user/favorites/${product.id}`);
      } else {
        return apiClient.post('/user/favorites', { product_id: product.id });
      }
    },
    onSuccess: () => {
      setIsFavorite(!isFavorite);
      queryClient.invalidateQueries(['favorite-check', product.id]);
      queryClient.invalidateQueries(['user-favorites']);
      queryClient.invalidateQueries(['favorites-count']);
      notification.success(isFavorite ? t('removedFromFavorites') : t('addedToFavorites'));
    },
    onError: (error) => {
      if (error.response?.status === 401) {
        notification.warning(t('loginRequiredFavorites'));
      } else {
        notification.error(error.response?.data?.error || 'Klaida');
      }
    },
  });

  const displayOriginalPrice = React.useMemo(() => {
    if (selectedVariant) {
      return selectedVariant.originalPrice || selectedVariant.price || null;
    }
    return product?.originalPrice || product?.compare_price || null;
  }, [product, selectedVariant]);

  React.useEffect(() => {
    if (!galleryRef.current) return;
    const el = galleryRef.current;
    const update = () => setGalleryWidth(el.offsetWidth || 0);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [product?.id]);

  if (isLoading) {
    return (
      <div className="product-detail product-detail-oa">
        <div className="loading">{t('loading')}</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail product-detail-oa">
        <div className="error">{t('productNotFound')}</div>
      </div>
    );
  }

  const images = product.images?.length ? product.images : [];
  const totalSlides = images.length || 1;

  const goPrev = () => {
    setSelectedImageIndex((i) => (i <= 0 ? totalSlides - 1 : i - 1));
  };
  const goNext = () => {
    setSelectedImageIndex((i) => (i >= totalSlides - 1 ? 0 : i + 1));
  };

  const slideWidthPx = galleryWidth * SLIDE_WIDTH_RATIO;
  const trackWidthPx = totalSlides * slideWidthPx + (totalSlides - 1) * GALLERY_GAP_PX;
  const trackOffsetPx = galleryWidth * (1 - SLIDE_WIDTH_RATIO) / 2 - selectedImageIndex * (slideWidthPx + GALLERY_GAP_PX);
  const swipeThresholdPx = Math.min(60, slideWidthPx * 0.2);

  const applyDragOffset = (deltaX) => {
    dragOffsetPxRef.current = deltaX;
    setDragOffsetPx(deltaX);
  };

  const finishDrag = () => {
    const offset = dragOffsetPxRef.current;
    applyDragOffset(0);
    if (offset > swipeThresholdPx) goPrev();
    else if (offset < -swipeThresholdPx) goNext();
  };

  const handleTouchStart = (e) => {
    if (images.length <= 1) return;
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
    applyDragOffset(0);
  };

  const handleTouchMove = (e) => {
    if (images.length <= 1) return;
    const t = e.touches[0];
    const deltaX = t.clientX - touchStartRef.current.x;
    const deltaY = t.clientY - touchStartRef.current.y;
    applyDragOffset(deltaX);
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 8) {
      e.preventDefault();
    }
  };

  const handleTouchEnd = () => {
    if (images.length <= 1) return;
    finishDrag();
  };

  const handleMouseDown = (e) => {
    if (images.length <= 1 || e.button !== 0) return;
    touchStartRef.current = { x: e.clientX, y: e.clientY };
    applyDragOffset(0);
    const onMouseMove = (e2) => {
      const deltaX = e2.clientX - touchStartRef.current.x;
      applyDragOffset(deltaX);
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      mouseListenersRef.current = null;
      finishDrag();
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    mouseListenersRef.current = { onMouseMove, onMouseUp };
  };

  const trackTransformPx = trackOffsetPx + dragOffsetPx;

  return (
    <div className="product-detail product-detail-oa">
      <div className="product-detail-container">
        <nav className="product-detail-breadcrumb" aria-label="Breadcrumb">
          <Link to="/products">{t('products')}</Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">{productName}</span>
        </nav>
      </div>

      {/* Pilno pločio hero: galerija per visą langą + informacijos blokas virš jos dešinėje (kaip O+A) */}
      <section className="product-detail-hero" aria-label="Prekės nuotraukos ir informacija">
        <div className="product-gallery">
          <div
            ref={galleryRef}
            className={`product-gallery-slider ${images.length > 1 ? 'product-gallery-slider-peek' : ''}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
            onMouseDown={handleMouseDown}
            style={images.length > 1 ? { cursor: dragOffsetPx !== 0 ? 'grabbing' : 'grab' } : undefined}
            role="region"
            aria-label="Nuotraukų galerija – slinkite pirštu arba pele"
          >
            {images.length > 0 ? (
              <>
                <div
                  className="product-gallery-track"
                  style={
                    images.length > 1 && galleryWidth > 0
                      ? {
                          width: `${trackWidthPx}px`,
                          transform: `translateX(${trackTransformPx}px)`,
                          transition: dragOffsetPx !== 0 ? 'none' : 'transform 0.35s ease-out',
                        }
                      : undefined
                  }
                >
                  {images.map((img, idx) => (
                    <div
                      key={idx}
                      className="product-gallery-slide"
                      style={
                        images.length > 1 && galleryWidth > 0
                          ? { width: `${slideWidthPx}px`, flex: '0 0 auto' }
                          : undefined
                      }
                    >
                      <img src={img.image_url} alt={`${productName} ${idx + 1}`} />
                    </div>
                  ))}
                </div>
                {images.length > 1 && (
                  <div className="product-gallery-dots">
                      {images.map((_, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`product-gallery-dot ${selectedImageIndex === idx ? 'active' : ''}`}
                          onClick={() => setSelectedImageIndex(idx)}
                          aria-label={`Nuotrauka ${idx + 1}`}
                        />
                      ))}
                  </div>
                )}
              </>
            ) : (
              <div className="product-gallery-slide no-image">{t('noImage')}</div>
            )}
          </div>
        </div>

        {/* Informacijos blokas virš galerijos dešinėje (overlay kaip O+A) */}
        <div className="product-info-card">
          <div className="product-info">
          <button
            type="button"
            onClick={() => toggleFavoriteMutation.mutate()}
            disabled={toggleFavoriteMutation.isLoading}
            className={`product-info-favorite ${isFavorite ? 'active' : ''}`}
            title={isFavorite ? t('removeFromFavorites') : t('addToFavorites')}
            aria-label={isFavorite ? t('removeFromFavorites') : t('addToFavorites')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </button>
          <h1 className="product-title">{productName}</h1>
          <div className="product-price-block">
            <span className="product-price">{parseFloat(displayPrice).toFixed(2)} €</span>
            {displayOriginalPrice && displayOriginalPrice !== displayPrice && (
              <span className="product-compare-price">{parseFloat(displayOriginalPrice).toFixed(2)} €</span>
            )}
            {saleInfo && (
              <span className="product-sale-badge">
                {saleInfo.salePercent ? `−${saleInfo.salePercent}%` : t('sale')}
              </span>
            )}
          </div>

          {groupedVariants && Object.keys(groupedVariants).length > 0 && (
            <div className="product-variants product-variants-compact">
              {Object.entries(groupedVariants).map(([attrName, values]) => (
                <div key={attrName} className="variant-row">
                  <span className="variant-label">{attrName}</span>
                  <div className="variant-pills">
                    {values.map((value) => {
                      const isSelected = selectedVariant?.attributes?.some(
                        a => (a.attribute_name || a.attributeName) === attrName && (a.attribute_value || a.attributeValue) === value
                      );
                      return (
                        <button
                          key={value}
                          type="button"
                          className={`variant-pill ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleAttributeChange(attrName, value)}
                        >
                          {value}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {showSimpleVariantList && (
            <div className="product-variants product-variants-compact">
              <span className="variant-label">{t('variant')}</span>
              <div className="variant-pills">
                {product.variants.map((v) => {
                  const isSelected = selectedVariant?.id === v.id;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      className={`variant-pill ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSimpleVariantSelect(v)}
                    >
                      {v.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* 4 eilutė – kiekis (stilius kaip Krepšelyje: − laukelis +) */}
          <div className="product-info-row-quantity">
            <span className="quantity-label">{t('quantity')}</span>
            <div className="quantity-controls">
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                disabled={quantity <= 1}
                className="quantity-btn"
                aria-label="-"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14"></path>
                </svg>
              </button>
              <input
                id="product-quantity"
                type="number"
                min="1"
                max={displayStock || 999}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Math.min(displayStock || 999, parseInt(e.target.value) || 1)))}
                className="quantity-input"
              />
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.min(displayStock || 999, q + 1))}
                disabled={quantity >= (displayStock || 999)}
                className="quantity-btn"
                aria-label="+"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14"></path>
                </svg>
              </button>
            </div>
          </div>

          {/* 5 eilutė – mygtukas į krepšelį (tas pats stilius kaip Krepšelio „Pirkti“ ir Checkout „Pateikti užsakymą“) */}
          <div className="product-actions">
            <button
              type="button"
              onClick={() => addToCartMutation.mutate()}
              disabled={displayStock === 0 || addToCartMutation.isLoading}
              className="product-detail-add-btn"
            >
              {addToCartMutation.isLoading ? t('adding') : t('addToCart')}
            </button>
          </div>
        </div>
        </div>
      </section>

      {/* Žemiau: du stulpeliai – kairė aprašymas, dešinė išskleidžiami blokai */}
      <div className="product-detail-container">
      <div className="product-detail-below">
        <div className="product-detail-two-col">
          <div className="product-detail-desc">
            <h2 className="product-detail-desc-title">{t('description')}</h2>
            {(productDescription || product.description || product.short_description) ? (
              <div
                className="product-detail-desc-body"
                dangerouslySetInnerHTML={{
                  __html: stripInlineStyles(productDescription || product.description || product.short_description || ''),
                }}
              />
            ) : (
              <p className="product-detail-desc-empty">—</p>
            )}
          </div>
          <div className="product-detail-extra">
            {(product.sku || product.barcode || product.length_cm || product.width_cm || product.height_cm) && (
              <div className="product-detail-meta">
                <div className="product-detail-meta-title">{t('information')}</div>
                {product.sku && (
                  <div className="product-detail-meta-row">
                    <span className="product-detail-meta-label">{t('sku')}</span>
                    <span className="product-detail-meta-value">{product.sku}</span>
                  </div>
                )}
                {product.barcode && (
                  <div className="product-detail-meta-row">
                    <span className="product-detail-meta-label">{t('barcode')}</span>
                    <span className="product-detail-meta-value">{product.barcode}</span>
                  </div>
                )}
                {(product.length_cm || product.width_cm || product.height_cm) && (
                  <div className="product-detail-meta-row">
                    <span className="product-detail-meta-label">{t('packageDimensions')}</span>
                    <span className="product-detail-meta-value">
                      {[product.length_cm, product.width_cm, product.height_cm].filter(Boolean).map((v) => Number(v)).join(' × ')} cm
                    </span>
                  </div>
                )}
              </div>
            )}
            {product.attributes && product.attributes.length > 0 && (
              <details className="product-detail-section">
                <summary>{t('specifications')}</summary>
                <div className="product-detail-section-inner">
                  <table className="spec-table">
                    <tbody>
                      {product.attributes.map((attr, idx) => (
                        <tr key={idx}>
                          <td>{attr[`attribute_name_${lang}`] || attr.attribute_name_lt || attr.attribute_name_en || attr.attribute_name_de || attr.attribute_name}</td>
                          <td>{attr[`attribute_value_${lang}`] || attr.attribute_value_lt || attr.attribute_value_en || attr.attribute_value_de || attr.attribute_value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
            {product.collections && product.collections.length > 0 && (
              <details className="product-detail-section">
                <summary>{t('collections')}</summary>
                <div className="product-detail-section-inner">
                  <p className="product-collections">
                    {product.collections.map((c, i) => (
                      <React.Fragment key={c.id}>
                        <Link to={`/collections/${c.slug}`}>{c.name}</Link>
                        {i < product.collections.length - 1 && ', '}
                      </React.Fragment>
                    ))}
                  </p>
                </div>
              </details>
            )}
            {product.bundles && product.bundles.length > 0 && (
              <details className="product-detail-section">
                <summary>{t('productIncludes')}</summary>
                <div className="product-detail-section-inner bundles-list">
                  {product.bundles.map((bundle) => (
                    <Link key={bundle.id} to={`/products/${bundle.included_product_slug}`} className="bundle-item">
                      {bundle.included_product_image && (
                        <img src={bundle.included_product_image} alt={bundle.included_product_name} />
                      )}
                      <div>
                        <strong>{bundle.included_product_name}</strong>
                        <span>{t('bundleQuantity')}: {bundle.quantity}</span>
                        {bundle.discount_percent > 0 && (
                          <span className="bundle-discount">{t('bundleDiscount')}: {bundle.discount_percent}%</span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </details>
            )}
            {product.videos && product.videos.length > 0 && (
              <details className="product-detail-section">
                <summary>{t('video')}</summary>
                <div className="product-detail-section-inner">
                  {product.videos.map((video) => (
                    <div key={video.id} className="video-item">
                      {video.video_type === 'youtube' && (
                        <iframe src={`https://www.youtube.com/embed/${video.video_url.split('/').pop()}`} frameBorder="0" allowFullScreen title={video.title || product.name} />
                      )}
                      {video.video_type === 'vimeo' && (
                        <iframe src={`https://player.vimeo.com/video/${video.video_url.split('/').pop()}`} frameBorder="0" allowFullScreen title={video.title || product.name} />
                      )}
                      {video.video_type === 'direct' && <video controls><source src={video.video_url} /></video>}
                      {video.title && <p className="video-title">{video.title}</p>}
                    </div>
                  ))}
                </div>
              </details>
            )}
            {product.reviews && product.reviews.length > 0 && (
              <details className="product-detail-section" open={false}>
                <summary>{t('reviews')} ({product.reviews.length})</summary>
                <div className="product-detail-section-inner">
                  <p className="product-reviews-summary">
                    {t('averageRating')}: {parseFloat(product.avgRating).toFixed(1)} {t('outOfFive')}
                  </p>
                  <div className="product-reviews-list">
                    {product.reviews.map((review) => (
                      <article key={review.id} className="product-review-card">
                        <div className="product-review-header">
                          <strong>{review.first_name} {review.last_name}</strong>
                          <span className="product-review-rating">{review.rating}/5</span>
                        </div>
                        {review.title && <h4 className="product-review-title">{review.title}</h4>}
                        <p className="product-review-text">{review.comment}</p>
                      </article>
                    ))}
                  </div>
                </div>
              </details>
            )}
          </div>
        </div>
      </div>

      {/* Cross-sell */}
      {product.crossSells && product.crossSells.length > 0 && (
        <section className="product-related">
          <h2 className="product-related-title">{t('youMayAlsoLike')}</h2>
          <div className="product-related-grid">
            {product.crossSells.map((item) => (
              <Link key={item.id} to={`/products/${item.slug}`} className="product-related-card">
                {item.primary_image && <img src={item.primary_image} alt={item.name} />}
                <h3>{item.name}</h3>
                <p className="product-related-price">
                  {item.salePrice ? (
                    <>
                      <span>{parseFloat(item.salePrice).toFixed(2)} €</span>
                      <span className="compare">{parseFloat(item.originalPrice || item.price).toFixed(2)} €</span>
                    </>
                  ) : (
                    parseFloat(item.price).toFixed(2) + ' €'
                  )}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Upsell */}
      {product.upsells && product.upsells.length > 0 && (
        <section className="product-related">
          <h2 className="product-related-title">{t('recommended')}</h2>
          <div className="product-related-grid">
            {product.upsells.map((item) => (
              <Link key={item.id} to={`/products/${item.slug}`} className="product-related-card">
                {item.primary_image && <img src={item.primary_image} alt={item.name} />}
                <h3>{item.name}</h3>
                <p className="product-related-price">
                  {item.salePrice ? (
                    <>
                      <span>{parseFloat(item.salePrice).toFixed(2)} €</span>
                      <span className="compare">{parseFloat(item.originalPrice || item.price).toFixed(2)} €</span>
                    </>
                  ) : (
                    parseFloat(item.price).toFixed(2) + ' €'
                  )}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      </div>
    </div>
  );
}

export default ProductDetail;
